export type TrackingMetadata = Record<
  string,
  string | number | boolean | string[] | null | undefined
>;
export type TrackingAction = {
  name: keyof typeof EVENT_NAMES;
  metadata?: TrackingMetadata;
};

/**
 * We include `integration` keyword in the analytics name,
 * to avoid the ambiguity of the events when visualizing events
 */
export const EVENT_NAMES = {
  // navigation
  NAVIGATED_TO_PATH: 'Navigated to integration path',
  SELECTED_TABLE: 'Selected integration tabular mapping table',
  SWITCH_TO_CLJS_INTEGRATION: 'Loaded integration clojurescript version',
  OPENED_ASSETS_OVERVIEW: 'Assets manager opened assets overview',

  // configurations
  CLEARED_CONFIG: 'Cleared current config',
  CLEARED_SHEET_CONFIG: 'Cleared sheet config',
  CLICKED_MANAGE_CONFIGURATION: 'Clicked managed integration configuration',
  LOADED_CONFIG: 'Loaded integration config',
  RESTARTED_IMPORT: 'Started new integration import flow',
  SAVED_CONFIGURATION: 'Saved integration configurations',
  TRIGGERED_CONFIG_SAVE_AS: 'Triggered current config save',
  TRIGGERED_CONFIG_SAVE: 'Triggered current config save as',

  // config step
  SELECTED_WORKSHEET_TYPE: 'Selected integration worksheet type',
  SELECTED_WORKSHEET_WORKSPACE: 'Selected integration worksheet workspace',
  APPLIED_COLUMN_MAPPING: 'Applied integration column mapping',
  TOGGLED_DATA_SYNC_STRATEGY_SECTION:
    'Interacted with integration Data Sync Strategy section',
  SELECT_DATA_SYNC_STRATEGY_OPTION:
    'Selected integration Data Sync Strategy update/create option',
  SELECT_DATA_SYNC_STRATEGY_HARD_DELETE_OPTION:
    'Selected integration Data Sync Strategy hard delete option',

  // config step - filters
  APPLIED_COLUMN_FILTERS: 'Applied integration column filters',
  CLEARED_COLUMN_FILTERS: 'Cleared integration column filters',
  OPENED_COLUMN_FILTER_MODAL: 'Opened integration column filters',
  OPENED_TRANSFORMATION_MODAL: 'Opened integration column transformations',
  OPENED_TRANSFORMATIONS: 'Integrations transformations0 opened transform data',
  OPENED_TRANSFORMATIONS_FILTERS:
    'Integrations transformations0 opened filter data',
  SELECTED_DICTIONARY_LOOKUP_TRANSFORMATION:
    'Integrations transformations0 selected dictionary lookup transformation',
  SELECTED_JSON_PATH_TRANSFORMATION:
    'Integrations transformations0 selected json path transformation',
  ROUND_NUMBER_TRANSFORMATION:
    'Integrations transformations0 round number transformation',
  ARITHMETIC_EXPRESSION_TRANSFORMATION:
    'Integrations transformations0 arithmetic expression transformation',
  AS_NUMBER_TRANSFORMATION:
    'Integrations transformations0 as number transformation',
  TO_UPPER_CASE_TRANSFORMATION:
    'Integrations transformations0 to upper case transformation',
  TO_LOWER_CASE_TRANSFORMATION:
    'Integrations transformations0 to lower case transformation',
  AS_DATE_TRANSFORMATION:
    'Integrations transformations0 as date transformation',
  AS_DATETIME_TRANSFORMATION:
    'Integrations transformations0 as datetime transformation',
  SUBSTRING_REPLACE_TRANSFORMATION:
    'Integrations transformations0 substring replace transformation',
  HTML_TO_MARKDOWN_TRANSFORMATION:
    'Integrations transformations0 HTML to Markdown transformation',
  CONCAT_TRANSFORMATION: 'Integrations transformations0 concat transformation',
  ADDED_TRANSFORMATIONS_DICTIONARY_RULE:
    'Integrations transformations0 added new dictionary rule',
  DELETED_TRANSFORMATIONS_DICTIONARY_RULE:
    'Integrations transformations0 deleted dictionary rule',
  DELETED_TRANSFORMATIONS:
    'Integrations transformations0 deleted transformations',
  ADDED_FILTER_OPTION: 'Integrations transformations0 added new filter option',
  DELETED_TRANSFORMATIONS_FILTER_OPTION:
    'Integrations transformations0 deleted filter option',
  OPENED_WORKSHEET_OVERVIEW: 'Opened integration worksheet overview',

  // import & review step
  ERRORED_IMPORT: 'Errored integration import',
  ERRORED_EXPORT: 'Errored integration export',
  ERRORED_TEST: 'Errored integration test',
  FAILED_IMPORT: 'Failed integration import',
  FAILED_EXPORT: 'Failed integration export',
  FAILED_TEST: 'Failed integration test',
  OPENED_REVIEW_AND_IMPORT: 'Opened integration review and import view',
  SUCCEEDED_IMPORT: 'Succeeded integration import',
  SUCCEEDED_EXPORT: 'Succeeded integration export',
  SUCCEEDED_TEST: 'Succeeded integration test',
  TRIGGERED_IMPORT: 'Triggered integration import',
  TRIGGERED_EXPORT: 'Triggered integration export',
  TRIGGERED_TEST: 'Triggered integration test',
  WARNED_IMPORT: 'Warned integration import',
  WARNED_EXPORT: 'Warned integration export',
  WARNED_TEST: 'Warned integration test',
  OPENED_DATA_AFTER_IMPORT: 'Opened integration data after import',

  // excel
  DOWNLOADED_TEMPLATE: 'Downloaded integration best practices template',
  OPENED_UPLOAD_VIEW: 'Opened integration upload view',
  REUPLOADED_FILE: 'Reuploaded integration file',
  TOGGLED_UPLOAD_KNOWLEDGE_SECTION:
    'Toggled integration upload knowledge section',
  UPLOADED_FILE_FAILURE: 'Uploaded integration file failure',
  UPLOADED_FILE_SUCCESS: 'Uploaded integration file',

  // actions
  CLICKED_OVERVIEW_ACTION: 'Clicked integration configurations overview action',

  // select step
  SELECTED_CONNECTION: 'Selected integration connection',
  SELECTED_SOURCE_FIELD: 'Selected integration source field',
  TRIGGERED_SOURCE_DATA_FETCH: 'Triggered integration source data fetch',
  IDENTIFIED_NON_SUPPORTED_RESOURCES:
    'Identified integration non-supported resources',

  // schedule
  SELECTED_SCHEDULE_INTERVAL: 'Selected integration schedule interval',
  ACTIVATED_SCHEDULE: 'Activated integration schedule',

  // import profile photos
  SUCCESS_IMPORT_PROFILE_PHOTOS: 'Success integration photos import',
  FAILURE_IMPORT_PROFILE_PHOTOS: 'Failure integration photos import',

  // connections
  CREATED_CONNECTION: 'Integrations created connection successfully',

  // dashboard
  VISITED_INTEGRATIONS_DASHBOARD_PAGE: 'Integrations dashboard0 visited page',
  CLICKED_VIEW_HISTORY: 'Integrations dashboard0 clicked view history',
  CLICKED_SCHEDULE: 'Integrations dashboard0 clicked schedule',
  CLICKED_VIEW_ALL_INTEGRATIONS:
    'Integrations dashboard0 clicked view all integrations',
  CLICKED_RECENT_INTEGRATION:
    'Integrations dashboard0 clicked recent integration',
  CLICKED_DATA_HISTORY_ENTRY:
    'Integrations dashboard0 clicked data history entry',
  USER_INPUT_MATCHES_HEADER: 'Tabular Import0 user input matches header',

  // lucid visual importer
  LUCID_DOCUMENT_ANALYZED: 'Lucid visual importer0 analyzed document',
  LUCID_VISUAL_IMPORT_TO_SCENARIO:
    'Lucid visual importer0 imported to scenario',
  LUCID_VISUAL_IMPORT_LLM_ERROR:
    'Lucid visual importer0 analyzed document LLM error',

  // import-builder
  IMPORT_BUILDER_PREVIEW_DATA_FETCHED: 'Import builder0 fetched preview data',
  IMPORT_BUILDER_PAGINATION_PREVIEW_FETCHED:
    'Import builder0 fetched pagination preview',
  IMPORT_BUILDER_CONNECTION_SAVED: 'Import builder0 saved connection',
  IMPORT_BUILDER_RESOURCE_DEFINITION_CHANGED:
    'Import builder0 changed resource definition',
  IMPORT_BUILDER_ALL_REQUESTS_RUN: 'Import builder0 run all requests',
  IMPORT_BUILDER_AI_CONFIGURE_REQUESTED:
    'Import builder0 requested AI configure',
  IMPORT_BUILDER_AI_CONFIGURE_SUCCEEDED:
    'Import builder0 generated AI configure',
  IMPORT_BUILDER_AI_CONFIGURE_FAILED: 'Import builder0 failed AI configure',

  VISUAL_IMPORTER_UPLOAD_STATUS_UPDATED:
    'Visual importer0 upload status updated',
  VISUAL_IMPORTER_SELECTED_LUCIDCHART:
    'Visual importer0 selected Lucidchart document',
  VISUAL_IMPORTER_FILE_UPLOAD_STARTED: 'Visual importer0 file upload started',
  VISUAL_IMPORTER_FILE_UPLOAD_SUCCEEDED:
    'Visual importer0 file upload succeeded',
  VISUAL_IMPORTER_FILE_UPLOAD_FAILED: 'Visual importer0 file upload failed',
  VISUAL_IMPORTER_ANALYSIS_COMPLETED: 'Visual importer0 completed analysis',
  VISUAL_IMPORTER_ANALYSIS_FAILED: 'Visual importer0 analysis failed',
  VISUAL_IMPORTER_REVIEWED_MAPPINGS: 'Visual importer0 reviewed mappings',
  VISUAL_IMPORTER_IMPORT_STARTED: 'Visual importer0 import started',
  VISUAL_IMPORTER_IMPORT_SUCCEEDED: 'Visual importer0 import succeeded',
  VISUAL_IMPORTER_IMPORT_FAILED: 'Visual importer0 import failed',

  // visual importer - component interactions
  VISUAL_IMPORTER_EDITED_COMPONENT: 'Visual importer0 edited component',
  VISUAL_IMPORTER_CHANGED_COMPONENT_TYPE:
    'Visual importer0 changed component type',
  VISUAL_IMPORTER_DELETED_COMPONENT: 'Visual importer0 deleted component',
  VISUAL_IMPORTER_ADDED_COMPONENT: 'Visual importer0 added component',

  // visual importer - reference interactions
  VISUAL_IMPORTER_EDITED_REFERENCE_TYPE:
    'Visual importer0 edited reference type',
  VISUAL_IMPORTER_EDITED_REFERENCE_TARGET:
    'Visual importer0 edited reference target',
  VISUAL_IMPORTER_DELETED_REFERENCE: 'Visual importer0 deleted reference',
  VISUAL_IMPORTER_ADDED_REFERENCE: 'Visual importer0 added reference',

  // visual importer - configuration
  VISUAL_IMPORTER_CHANGED_WORKSPACE_SELECTION:
    'Visual importer0 changed workspace selection',

  // overview segment events
  SWITCHED_OVERVIEW_SEGMENT: 'Switched integration overview segment',
  DISMISSED_METAMODEL_HINT: 'Dismissed integration metamodel hint',
};
